import React from "react";
import { useHistory, useLocation } from "react-router-dom";

export function getSearchParamsForLocation(locationSearch, defaultSearchParams) {
	let searchParams = createSearchParams(locationSearch);

	if (defaultSearchParams) {
		// Use `defaultSearchParams.forEach(...)` here instead of iterating of
		// `defaultSearchParams.keys()` to work-around a bug in Firefox related to
		// web extensions. Relevant Bugzilla tickets:
		// https://bugzilla.mozilla.org/show_bug.cgi?id=1414602
		// https://bugzilla.mozilla.org/show_bug.cgi?id=1023984
		defaultSearchParams.forEach((_, key) => {
			if (!searchParams.has(key)) {
				defaultSearchParams.getAll(key).forEach((value) => {
					searchParams.append(key, value);
				});
			}
		});
	}

	return searchParams;
}

export function createSearchParams(init = "") {
	return new URLSearchParams(
		typeof init === "string" || Array.isArray(init) || init instanceof URLSearchParams
			? init
			: Object.keys(init).reduce((memo, key) => {
					let value = init[key];
					return memo.concat(Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]);
			  }, [])
	);
}

export function useSearchParams(defaultInit) {
	if (typeof URLSearchParams === "undefined")
		console.warn(
			`You cannot use the \`useSearchParams\` hook in a browser that does not ` +
				`support the URLSearchParams API. If you need to support Internet ` +
				`Explorer 11, we recommend you load a polyfill such as ` +
				`https://github.com/ungap/url-search-params\n\n` +
				`If you're unsure how to load polyfills, we recommend you check out ` +
				`https://polyfill.io/v3/ which provides some recommendations about how ` +
				`to load polyfills only for users that need them, instead of for every ` +
				`user.`
		);

	let defaultSearchParamsRef = React.useRef(createSearchParams(defaultInit));
	let hasSetSearchParamsRef = React.useRef(false);

	let location = useLocation();
	let searchParams = React.useMemo(
		() =>
			// Only merge in the defaults if we haven't yet called setSearchParams.
			// Once we call that we want those to take precedence, otherwise you can't
			// remove a param with setSearchParams({}) if it has an initial value
			getSearchParamsForLocation(location.search, hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current),
		[location.search]
	);

	let history = useHistory();
	let setSearchParams = React.useCallback(
		(nextInit, navigateOptions) => {
			const newSearchParams = createSearchParams(typeof nextInit === "function" ? nextInit(searchParams) : nextInit);
			hasSetSearchParamsRef.current = true;
			history.replace("?" + newSearchParams, navigateOptions);
		},
		[history, searchParams]
	);

	return [searchParams, setSearchParams];
}
